/**
 * Backend-driven dispatch — one ``UIComponent`` per renderable block.
 *
 * Every block payload that arrives over the chat stream or through
 * ``GET /api/v3/chats/{id}/messages`` carries a ``ui_component_type``
 * tag. The frontend's component registry keys on this enum: there is
 * no string-to-component switch on the frontend side. This is the
 * single source of truth for how the registry dispatches a payload,
 * mirrored byte-for-byte from
 * ``backend/app/modules/chats/schemas/ui_component.py::UIComponent``.
 *
 * Adding a new member is an additive, non-breaking change. Renaming or
 * removing a member is an API break — older messages persisted with
 * the prior tag would render as nothing.
 */

export const UIComponent = {
	TEXT: "text",
	THINKING: "thinking",

	// Trace blocks
	TOOL_CALL: "tool_call",
	TOOL_RESULT_MARKDOWN: "tool_result_markdown",
	TOOL_RESULT_JSON_TREE: "tool_result_json_tree",
	TOOL_RESULT_DEAL_ROOM: "tool_result_deal_room",
	TOOL_RESULT_WEB_SEARCH: "tool_result_web_search",
	TOOL_RESULT_DOCUMENT_QA: "tool_result_document_qa",
	TOOL_RESULT_MANIPULATE_WORKBOOK: "tool_result_manipulate_workbook",
	PHASE_BANNER: "phase_banner",
	TRACE_ERROR_BANNER: "trace_error_banner",
	MEMO_GENERATED_BANNER: "memo_generated_banner",

	// Evidence cards (one per EvidenceEvent.kind)
	EVIDENCE_PDF_FACT: "evidence_pdf_fact",
	EVIDENCE_WEB_SOURCE: "evidence_web_source",
	EVIDENCE_EXCEL_CHANGE: "evidence_excel_change",
	EVIDENCE_CALCULATION: "evidence_calculation",
	EVIDENCE_COMMS_MESSAGE: "evidence_comms_message",
	EVIDENCE_RECORD_FIELD: "evidence_record_field",

	// Charts / overflow / artifacts
	CHART_LINE: "chart_line",
	CHART_BAR: "chart_bar",
	CHART_AREA: "chart_area",
	CHART_PIE: "chart_pie",
	CHART_SCATTER: "chart_scatter",
	TABLE_OVERFLOW_PENDING: "table_overflow_pending",
	TABLE_OVERFLOW: "table_overflow",
	CHART_OVERFLOW_PENDING: "chart_overflow_pending",
	CHART_OVERFLOW: "chart_overflow",
	MODIFIED_WORKBOOK_CARD: "modified_workbook_card",
	SCHEMA_GRAPH_CARD: "schema_graph_card",
	LOAN_TAPE_PREVIEW_CARD: "loan_tape_preview_card",
	CITATION_HARNESS_REPORT: "citation_harness_report",

	// Internal sentinel — the streaming worker lifts blocks tagged with
	// this into top-level ``message_metadata.evidence`` rather than
	// rendering them. Customer UI must never receive a block carrying
	// this tag over the public stream.
	INTERNAL_EVIDENCE_METADATA: "internal_evidence_metadata",

	// Citation-artifact debug trace — rendered in the trace panel (shows
	// coverage + grouped per-cell verification failure reasons).
	CITATION_ARTIFACT_METADATA: "citation_artifact_metadata",
} as const;
export type UIComponent = (typeof UIComponent)[keyof typeof UIComponent];

/**
 * Map an ``EvidenceEvent.kind`` to its renderable ``UIComponent``.
 *
 * Backend serializers tag every public evidence event with the same
 * value, so the FE can dispatch on ``ui_component_type`` directly.
 * This lookup is the fallback for legacy events that don't yet carry
 * the field.
 */
export const EVIDENCE_KIND_TO_UI_COMPONENT: Record<string, UIComponent> = {
	pdf_fact: UIComponent.EVIDENCE_PDF_FACT,
	web_source: UIComponent.EVIDENCE_WEB_SOURCE,
	excel_change: UIComponent.EVIDENCE_EXCEL_CHANGE,
	calculation: UIComponent.EVIDENCE_CALCULATION,
	comms_fact: UIComponent.EVIDENCE_COMMS_MESSAGE,
	record_fact: UIComponent.EVIDENCE_RECORD_FIELD,
};

/** Map a ``ChartBlock.chart_type`` string to a ``UIComponent``. */
export const CHART_TYPE_TO_UI_COMPONENT: Record<string, UIComponent> = {
	line: UIComponent.CHART_LINE,
	bar: UIComponent.CHART_BAR,
	area: UIComponent.CHART_AREA,
	pie: UIComponent.CHART_PIE,
	scatter: UIComponent.CHART_SCATTER,
};
